import { ReactNode } from 'react';
import Image from 'next/image';

import { Badge, Box, BoxProps, Flex, Heading, LinkBox, LinkOverlay, Skeleton, Text } from '@chakra-ui/react';
import {
  Difficulty1Icon,
  Difficulty2Icon,
  Difficulty3Icon,
  HistoryIcon,
  StarFullIcon,
} from '@udacity/chakra-uds-icons';

import { Card } from '~/components/card';
import { ContentContainer } from '~/components/container';
import { trackEvent } from '~/features/analytics/track-event';
import { useCatalogItems } from '~/features/catalog/hooks/use-catalog-items';
import { CatalogItem } from '~/features/catalog/models/catalog-item';
import { REVIEW_MINIMUM_THRESHOLD } from '~/features/program/constants';
import { useCourseLink } from '~/features/program/hooks/use-course-link';

export type SkillLevelCollectionProps = {
  title: string;
  skillLevel: 'beginner' | 'intermediate' | 'advanced';
  badgeText: string;
  theme?: 'sea-foam' | 'blue';
  blurb: string;
  programKeys: string[];
  imageSlot: ReactNode;
  imagePosition?: 'left' | 'right';
  analyticsLabel: string;
};

export function SkillLevelCollection({
  title,
  skillLevel,
  badgeText,
  theme = 'blue',
  blurb,
  programKeys,
  imageSlot = null,
  imagePosition = 'right',
  analyticsLabel,
}: SkillLevelCollectionProps) {
  const { catalogItems, catalogItemsIsLoading } = useCatalogItems(programKeys);

  const renderSkillLevelIcon = () => {
    switch (skillLevel) {
      case 'beginner':
        return <Difficulty1Icon />;
      case 'intermediate':
        return <Difficulty2Icon />;
      case 'advanced':
        return <Difficulty3Icon />;
      default:
        return null;
    }
  };

  return (
    <Box
      as='section'
      bgColor={theme === 'sea-foam' ? '#EAF9F6' : 'accent.sky.100'}
      py={{
        base: 10,
        xl: 20,
      }}
    >
      <ContentContainer
        alignItems='center'
        display='flex'
        flexDir={
          imagePosition === 'right'
            ? 'row'
            : {
                base: 'row',
                xl: 'row-reverse',
              }
        }
        gap={{
          base: 8,
          xl: 16,
        }}
      >
        <Flex flex='1' flexDir='column' gap={4}>
          <Badge
            alignItems='center'
            alignSelf='flex-start'
            bgColor={theme === 'sea-foam' ? 'accent.sea-foam.700' : 'blue.500'}
            color='white'
            display='inline-flex'
            gap={2}
            whiteSpace='nowrap'
          >
            {renderSkillLevelIcon()}
            {badgeText}
          </Badge>
          <Box>
            <Heading as='h2' size='marketing-4xl'>
              {title}
            </Heading>
            <Text mb={10}>{blurb}</Text>
            <Flex>
              <Flex flex='1' flexDir='column' gap={4}>
                {catalogItemsIsLoading && (
                  <>
                    <SkeletonProgramCard />
                    <SkeletonProgramCard />
                    <SkeletonProgramCard />
                    <SkeletonProgramCard />
                  </>
                )}
                {!catalogItemsIsLoading &&
                  catalogItems?.map((catalogItem) => (
                    <ProgramCard
                      {...catalogItem}
                      key={catalogItem.key}
                      onClick={() =>
                        trackEvent({
                          type: 'click',
                          name: 'skills_page_cta_clicked',
                          label: analyticsLabel,
                          value: catalogItem.title || '',
                        })
                      }
                    />
                  ))}
              </Flex>
            </Flex>
          </Box>
        </Flex>
        <Box
          alignSelf='flex-end'
          display={{
            base: 'none',
            md: 'block',
          }}
          flex={{
            md: '1',
            xl: '0 1 451px',
          }}
        >
          {imageSlot}
        </Box>
      </ContentContainer>
    </Box>
  );
}

function ProgramCard({
  image,
  title,
  rawDurationDisplay,
  reviewSummary,
  slug,
  onClick,
}: CatalogItem & { onClick?: () => void }) {
  const courseLink = useCourseLink({ courseSlug: slug });
  if (!image || !title) return null;
  return (
    <LinkBox
      borderRadius='lg'
      role='group'
      transition='all 0.3s ease-in-out'
      _hover={{
        boxShadow: 'lg',
      }}
    >
      <ProgramCardLayout
        imageSlot={
          <Box
            alignSelf='normal'
            position='relative'
            width={{
              base: '135px',
              xl: '216px',
            }}
          >
            <Image
              alt=''
              sizes='268px'
              src={image}
              style={{
                objectFit: 'cover',
                pointerEvents: 'none',
              }}
              fill
            />
            <Box
              bgGradient='linear(to-b, transparent, white, transparent)'
              bottom={0}
              height='120%'
              mixBlendMode='soft-light'
              opacity={0.3}
              pointerEvents='none'
              position='absolute'
              right={0}
              transform='translateX(50%) translateY(100%) rotate(-20deg)'
              transition='transform 0.3s ease-in-out'
              width='200%'
              _groupHover={{
                transform: 'translateX(10%) translateY(-10%) rotate(-20deg)',
              }}
            />
          </Box>
        }
        statsSlot={
          <>
            <Box>
              <Flex alignItems='center' gap={1} hidden={!rawDurationDisplay}>
                <HistoryIcon height='24px' width='24px' />
                <Text size='caption'>{rawDurationDisplay}</Text>
              </Flex>
            </Box>
            {reviewSummary && reviewSummary?.numberOfReviews >= REVIEW_MINIMUM_THRESHOLD && (
              <Flex
                gap={{
                  base: 1,
                  xl: 2,
                }}
              >
                <Flex alignItems='center' gap={1}>
                  <Text size='label'>{reviewSummary.starsAverage}</Text>
                  <StarFullIcon color='blue.500' height='20px' width='20px' />
                </Flex>
                <Text
                  hidden={reviewSummary.numberOfReviews === 0}
                  display={{
                    base: 'none',
                    xl: 'block',
                  }}
                  sx={{
                    '&::before': {
                      content: "'('",
                    },
                    '&::after': {
                      content: "')'",
                    },
                  }}
                >
                  {reviewSummary.numberOfReviews} reviews
                </Text>
              </Flex>
            )}
          </>
        }
        titleSlot={
          <Heading
            as={LinkOverlay}
            fontWeight='400'
            href={courseLink}
            noOfLines={4}
            _focus={{
              outline: 'none',
            }}
            size={{
              base: 'marketing-md',
              xl: 'marketing-xl',
            }}
            onClick={onClick}
          >
            {title}
          </Heading>
        }
      />
    </LinkBox>
  );
}

function ProgramCardLayout({
  imageSlot = null,
  titleSlot = null,
  statsSlot = null,
  ...rest
}: {
  imageSlot: ReactNode;
  titleSlot: ReactNode;
  statsSlot: ReactNode;
} & BoxProps) {
  return (
    <Card
      as='article'
      backgroundColor='white'
      borderColor='gray.200'
      borderRadius='lg'
      display='flex'
      flexDir='row'
      overflow='hidden'
      width='full'
      _focusWithin={{
        outline: '2px solid',
        outlineColor: 'blue.500',
        outlineOffset: '4px',
      }}
      alignItems={{
        xl: 'center',
      }}
      minHeight={{
        base: '112px',
        xl: '120px',
      }}
      {...rest}
    >
      {imageSlot}
      <Flex
        flex='1'
        flexDir='column'
        gap={2}
        justifyContent='space-between'
        p={{
          base: 4,
          xl: 6,
        }}
      >
        {titleSlot}
        <Flex gap={2} justifyContent='space-between'>
          {statsSlot}
        </Flex>
      </Flex>
    </Card>
  );
}

function SkeletonProgramCard() {
  return (
    <ProgramCardLayout
      as='div'
      titleSlot={<Skeleton height='30px' maxWidth='296px' width='full' />}
      imageSlot={
        <Skeleton
          borderBottomRightRadius={0}
          borderTopRightRadius={0}
          height='full'
          width='full'
          flex={{
            base: '0 1 135px',
            xl: '0 1 216px',
          }}
        />
      }
      statsSlot={
        <>
          <Flex alignItems='center' gap={1}>
            <Skeleton height='20px' width='20px' />
            <Skeleton height='20px' width='62px' />
          </Flex>
          <Flex gap={2}>
            <Flex alignItems='center' gap={1}>
              <Skeleton height='20px' width='23px' />
              <Skeleton height='20px' width='20px' />
            </Flex>
            <Skeleton flex='0 1 86px' height='20px' />
          </Flex>
        </>
      }
    />
  );
}
